import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Loader2, Gift, ArrowLeft, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { giftPublicConfig, giftPurchaseIntent, formatIls, type GiftCardSettings } from '@/lib/giftCards';

type Config = Partial<GiftCardSettings> & { purchase_enabled: boolean };

export default function GiftCardPurchase() {
  const [config, setConfig] = useState<Config | null>(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState<number | null>(null);
  const [customAmount, setCustomAmount] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [senderName, setSenderName] = useState('');
  const [message, setMessage] = useState('');
  const [occasion, setOccasion] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [handedOff, setHandedOff] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await giftPublicConfig();
        setConfig(cfg);
        setAmount(Number(cfg.presets_ils?.[0] ?? 250));
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const effectiveAmount = useMemo(() => {
    if (customAmount.trim()) {
      const v = Number(customAmount);
      return Number.isFinite(v) ? v : 0;
    }
    return amount ?? 0;
  }, [customAmount, amount]);

  // Client-side hints only. The server re-validates the amount, the unit price
  // and the quantity, and it is the only thing that decides what is charged.
  const withinRange =
    !!config &&
    effectiveAmount >= Number(config.min_amount_ils ?? 0) &&
    effectiveAmount <= Number(config.max_amount_ils ?? Infinity);

  const checkout = async () => {
    if (!recipientName.trim()) return toast.error('נא להזין שם מקבל/ת');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(recipientEmail.trim())) return toast.error('נא להזין מייל תקין של המקבל/ת');
    if (!withinRange) return toast.error('הסכום אינו בטווח המותר');
    setSubmitting(true);
    try {
      const res = await giftPurchaseIntent({
        amount: effectiveAmount,
        recipient_name: recipientName.trim(),
        recipient_email: recipientEmail.trim(),
        sender_name: senderName.trim() || null,
        message: message.trim() || null,
        occasion: occasion || null,
      });
      if (!res.checkout_url) throw new Error('יצירת ההזמנה נכשלה');
      window.open(res.checkout_url, '_blank');
      setHandedOff(true);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'משהו לא עבד, נסו שוב');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-[#f4ece2]">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  return (
    <div dir="rtl" className="bg-[#412018] min-h-[80vh] py-16 px-5">
      <Helmet>
        <title>שובר מתנה | קליניקת ריהאם מנסור</title>
        <meta
          name="description"
          content="שובר מתנה דיגיטלי לקליניקת ריהאם מנסור — לטיפולים ולמוצרי טיפוח. נשלח במייל עם הקדשה אישית."
        />
        <link rel="canonical" href="https://riham.co.il/gift-card" />
      </Helmet>

      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <Gift className="h-8 w-8 mx-auto text-[#fbbf25] mb-4" />
          <h1 className="text-3xl md:text-4xl text-[#f4ece2] tracking-wide">{config?.headline_he ?? 'שובר מתנה'}</h1>
          <p className="text-[#f4ece2]/70 mt-3 leading-relaxed">
            מתנה שמרגישה נכון — לטיפולים ולמוצרי הטיפוח של הקליניקה. השובר נשלח במייל עם הקדשה אישית.
          </p>
        </div>

        {!config?.purchase_enabled ? (
          <div className="rounded-2xl bg-[#f7f3ea] p-8 text-center text-[#412018]">
            <div className="text-lg font-medium mb-2">רכישת שוברים באתר תיפתח בקרוב</div>
            <p className="text-sm text-[#412018]/70">
              בינתיים נשמח להנפיק שובר ידנית — צרו איתנו קשר ונשלח אותו למקבל/ת ישירות במייל.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-2 mt-5 rounded-full bg-[#412018] text-[#f7f3ea] px-6 py-3 text-sm"
            >
              לפנייה <ArrowLeft className="h-4 w-4" />
            </a>
          </div>
        ) : handedOff ? (
          <div className="rounded-2xl bg-[#f7f3ea] p-8 text-center text-[#412018] space-y-3">
            <ShieldCheck className="h-7 w-7 mx-auto text-[#4b7a52]" />
            <div className="text-lg font-medium">העברנו אותך לתשלום המאובטח בשופיפיי</div>
            <p className="text-sm text-[#412018]/70 leading-relaxed">
              השובר יונפק וישלח למקבל/ת במייל רק לאחר אישור התשלום. אם החלון נסגר — אפשר לפתוח אותו שוב או להתחיל רכישה
              חדשה.
            </p>
            <button
              onClick={() => setHandedOff(false)}
              className="mt-2 rounded-full border border-[#412018]/30 px-6 py-2.5 text-sm"
            >
              חזרה לטופס
            </button>
          </div>
        ) : (
          <div className="rounded-2xl bg-[#f7f3ea] p-6 md:p-8 space-y-6 text-[#412018]">
            <div>
              <div className="text-sm mb-2">בחירת סכום</div>
              <div className="flex flex-wrap gap-2">
                {(config.presets_ils ?? []).map((p) => (
                  <button
                    key={String(p)}
                    onClick={() => {
                      setCustomAmount('');
                      setAmount(Number(p));
                    }}
                    className={`px-5 py-2.5 rounded-full border text-sm transition ${
                      !customAmount && amount === Number(p)
                        ? 'bg-[#412018] text-[#f7f3ea] border-[#412018]'
                        : 'border-[#412018]/25 hover:border-[#412018]/60'
                    }`}
                  >
                    {formatIls(Number(p))}
                  </button>
                ))}
              </div>
              {config.allow_custom_amount && (
                <input
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value.replace(/[^\d]/g, ''))}
                  placeholder={`סכום אחר (${config.min_amount_ils}–${config.max_amount_ils} ₪)`}
                  inputMode="numeric"
                  className="mt-3 w-full rounded-lg border border-[#412018]/20 bg-white px-4 py-3 text-sm"
                />
              )}
              {!withinRange && effectiveAmount > 0 && (
                <p className="text-xs text-[#9c3b2b] mt-2">
                  הסכום חייב להיות בין {config.min_amount_ils} ל-{config.max_amount_ils} ₪.
                </p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <LabeledInput label="שם המקבל/ת" value={recipientName} onChange={setRecipientName} />
              <LabeledInput label="מייל המקבל/ת" value={recipientEmail} onChange={setRecipientEmail} ltr />
              <LabeledInput label="שם השולח/ת" value={senderName} onChange={setSenderName} />
              <div className="space-y-1.5">
                <label className="text-xs text-[#412018]/70">אירוע</label>
                <select
                  value={occasion}
                  onChange={(e) => setOccasion(e.target.value)}
                  className="w-full rounded-lg border border-[#412018]/20 bg-white px-4 py-3 text-sm"
                >
                  <option value="">ללא</option>
                  {(config.occasions ?? []).map((o) => (
                    <option key={o} value={o}>
                      {o}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-xs text-[#412018]/70">הקדשה אישית</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={3}
                maxLength={400}
                className="w-full rounded-lg border border-[#412018]/20 bg-white px-4 py-3 text-sm"
              />
            </div>

            <button
              onClick={checkout}
              disabled={submitting || !withinRange}
              className="w-full rounded-full bg-[#412018] text-[#f7f3ea] py-4 text-sm tracking-wide disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              מעבר לתשלום מאובטח · {formatIls(effectiveAmount)}
            </button>

            <p className="text-[11px] leading-relaxed text-[#412018]/60">{config.terms_he}</p>
          </div>
        )}
      </div>
    </div>
  );
}

function LabeledInput({
  label,
  value,
  onChange,
  ltr,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  ltr?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-xs text-[#412018]/70">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        dir={ltr ? 'ltr' : undefined}
        className="w-full rounded-lg border border-[#412018]/20 bg-white px-4 py-3 text-sm"
      />
    </div>
  );
}
